/**
 * AIChatShellProvider — owns the Shell slice (isChatbotOpen, chatbotWidth, isDevMode) via its
 * own useReducer.
 *
 * One of the four nested slice providers mounted by AIChatProvider. Consumers read the slice
 * through the named hook useAIChatShell() (state + actions) or useAIChatShellActions()
 * (actions only, via a dedicated context so pure-dispatcher consumers never re-render on state
 * changes). There is no merged value: each slice context is subscribed to directly.
 *
 * Action gating: `areActionsEnabled` (AIChatProvider passes isAIEnabled) gates the exposed
 * action set at call time via useGatedActions — state stays readable while writes are suppressed,
 * and the exposed identities never change. The slice itself is generic and knows nothing about
 * AI capability.
 */
import PropTypes from 'prop-types';
import React, { createContext, useMemo, useReducer } from 'react';
import { useGatedActions } from '../useGatedActions';
import { SHELL_ACTIONS, getInitialShellState, shellReducer } from './aiChatShellReducer';
import type { ShellState } from './aiChatShellTypes';

export type AIChatShellActions = {
  openChatbot: () => void;
  closeChatbot: () => void;
  toggleChatbot: () => void;
  setChatbotWidth: (width: number) => void;
};

export type AIChatShellValue = ShellState & AIChatShellActions;

export const AIChatShellContext = createContext<AIChatShellValue | null>(null);

/**
 * Actions-only context. Carries the SAME gated action object folded into the merged value, so
 * consumers that only dispatch (never read state) can subscribe here and skip state re-renders.
 */
export const AIChatShellActionsContext = createContext<AIChatShellActions | null>(null);

type AIChatShellProviderProps = {
  areActionsEnabled?: boolean;
  children: React.ReactNode;
  initialState?: Partial<ShellState>;
};

function AIChatShellProvider({ areActionsEnabled = true, children, initialState = {} }: AIChatShellProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(shellReducer, initialState, getInitialShellState);

  // Setters memoized once (deps `[]`) so their identity is stable for the component's lifetime —
  // `dispatch` is stable, so there is no reason to rebuild them per state change. Keeping them out
  // of the `[state]`-keyed value memo avoids churning consumer useCallback/useEffect deps.
  // `toggleChatbot` dispatches a self-contained TOGGLE action (the reducer flips the flag) so it
  // no longer needs to read `state.isChatbotOpen` and can stay in the stable memo.
  const actions = useMemo<AIChatShellActions>(
    () => ({
      openChatbot: () => dispatch({ type: SHELL_ACTIONS.OPEN_CHATBOT }),
      closeChatbot: () => dispatch({ type: SHELL_ACTIONS.CLOSE_CHATBOT }),
      toggleChatbot: () => dispatch({ type: SHELL_ACTIONS.TOGGLE_CHATBOT }),
      setChatbotWidth: (width: number) => dispatch({ type: SHELL_ACTIONS.SET_CHATBOT_WIDTH, payload: width }),
    }),
    []
  );

  // Call-time gating keeps the exposed identities permanently stable: consumers that capture the
  // set once (empty-dep callbacks/effects) keep dispatching correctly after areActionsEnabled flips.
  const exposedActions = useGatedActions(actions, areActionsEnabled);

  const value = useMemo<AIChatShellValue>(() => ({ ...state, ...exposedActions }), [state, exposedActions]);

  return (
    <AIChatShellActionsContext.Provider value={exposedActions}>
      <AIChatShellContext.Provider value={value}>{children}</AIChatShellContext.Provider>
    </AIChatShellActionsContext.Provider>
  );
}

AIChatShellProvider.propTypes = {
  areActionsEnabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  initialState: PropTypes.object,
};

export default AIChatShellProvider;
